import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePermissions } from "@/hooks/usePermissions";
import { canAny } from "@/utils/permissions";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "./DashboardHeader";
import { DASHBOARD_TABS, type DashboardTabId } from "./dashboard-tabs";
import {
  downloadDashboardReport,
  fetchOverviewDashboardData,
} from "./fetchers/overviewFetcher";
import type { DateRangePreset } from "./utils";
import { DashboardSkeleton } from "@/components/ui/dashboard-skeleton";

const DashboardOverviewTab = lazy(() =>
  import("./tabs/DashboardOverviewTab").then((m) => ({ default: m.DashboardOverviewTab }))
);
const DashboardRevenueTab = lazy(() =>
  import("./tabs/DashboardRevenueTab").then((m) => ({ default: m.DashboardRevenueTab }))
);
const DashboardEnrollmentsTab = lazy(() =>
  import("./tabs/DashboardEnrollmentsTab").then((m) => ({
    default: m.DashboardEnrollmentsTab,
  }))
);
const DashboardOperationsTab = lazy(() =>
  import("./tabs/DashboardOperationsTab").then((m) => ({
    default: m.DashboardOperationsTab,
  }))
);
const DashboardSupportTab = lazy(() =>
  import("./tabs/DashboardSupportTab").then((m) => ({ default: m.DashboardSupportTab }))
);
const DashboardBookstoreTab = lazy(() =>
  import("./tabs/DashboardBookstoreTab").then((m) => ({ default: m.DashboardBookstoreTab }))
);
const DashboardReportsTab = lazy(() =>
  import("./tabs/DashboardReportsTab").then((m) => ({ default: m.DashboardReportsTab }))
);

function TabFallback() {
  return <DashboardSkeleton showStudents={false} showPrograms={false} />;
}

export function DashboardPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { permissions, adminType, loading: permissionsLoading } = usePermissions();

  const visibleTabs = useMemo(
    () => DASHBOARD_TABS.filter((tab) => canAny(permissions, tab.permissions, adminType)),
    [permissions, adminType]
  );

  const [activeTab, setActiveTab] = useState<DashboardTabId>("overview");
  const [loadedTabs, setLoadedTabs] = useState<Set<DashboardTabId>>(new Set(["overview"]));
  const [datePreset, setDatePreset] = useState<DateRangePreset>("30d");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [overviewData, setOverviewData] = useState<Awaited<
    ReturnType<typeof fetchOverviewDashboardData>
  > | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    setLoadedTabs((prev) => new Set(prev).add(activeTab));
  }, [activeTab]);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const data = await fetchOverviewDashboardData(datePreset);
      setOverviewData(data);
      setLastUpdated(new Date());
    } catch (err) {
      setOverviewError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setOverviewLoading(false);
    }
  }, [datePreset, refreshToken]);

  useEffect(() => {
    if (isAuthenticated && !authLoading && loadedTabs.has("overview")) {
      void loadOverview();
    }
  }, [isAuthenticated, authLoading, loadedTabs, loadOverview]);

  const handleRefresh = () => {
    setRefreshToken((t) => t + 1);
  };

  const handleExport = async () => {
    try {
      await downloadDashboardReport(datePreset);
    } catch (err) {
      setOverviewError(err instanceof Error ? err.message : "Failed to export report");
    }
  };

  const canExport = canAny(
    permissions,
    ["dashboard.analytics.export", "dashboard.main.downloadReport"],
    adminType
  );

  if (authLoading || permissionsLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <DashboardSkeleton />
      </div>
    );
  }

  const tabProps = {
    datePreset,
    refreshToken,
    isActive: true,
  };

  return (
    <div className="flex flex-col gap-4 p-4 md:p-8">
      <DashboardHeader
        lastUpdated={lastUpdated}
        isRefreshing={overviewLoading && activeTab === "overview"}
        datePreset={datePreset}
        onDatePresetChange={setDatePreset}
        onRefresh={handleRefresh}
        onDownload={handleExport}
        showDownload={canExport}
      />

      {overviewError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p className="text-sm">{overviewError}</p>
          </div>
          <Button variant="outline" size="sm" className="mt-2" onClick={handleRefresh}>
            Try again
          </Button>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as DashboardTabId)}
        className="space-y-4"
      >
        <TabsList className="flex h-auto flex-wrap justify-start gap-1">
          {visibleTabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {loadedTabs.has("overview") && (
          <TabsContent value="overview" className="space-y-6">
            <Suspense fallback={<TabFallback />}>
              <DashboardOverviewTab
                {...tabProps}
                data={overviewData}
                loading={overviewLoading}
                onRefresh={loadOverview}
              />
            </Suspense>
          </TabsContent>
        )}

        {loadedTabs.has("revenue") && (
          <TabsContent value="revenue" className="space-y-6">
            <Suspense fallback={<TabFallback />}>
              <DashboardRevenueTab {...tabProps} />
            </Suspense>
          </TabsContent>
        )}

        {loadedTabs.has("enrollments") && (
          <TabsContent value="enrollments" className="space-y-6">
            <Suspense fallback={<TabFallback />}>
              <DashboardEnrollmentsTab {...tabProps} />
            </Suspense>
          </TabsContent>
        )}

        {loadedTabs.has("operations") && (
          <TabsContent value="operations" className="space-y-6">
            <Suspense fallback={<TabFallback />}>
              <DashboardOperationsTab {...tabProps} />
            </Suspense>
          </TabsContent>
        )}

        {loadedTabs.has("support") && (
          <TabsContent value="support" className="space-y-6">
            <Suspense fallback={<TabFallback />}>
              <DashboardSupportTab {...tabProps} />
            </Suspense>
          </TabsContent>
        )}

        {loadedTabs.has("bookstore") && (
          <TabsContent value="bookstore" className="space-y-6">
            <Suspense fallback={<TabFallback />}>
              <DashboardBookstoreTab {...tabProps} />
            </Suspense>
          </TabsContent>
        )}

        {loadedTabs.has("reports") && (
          <TabsContent value="reports" className="space-y-6">
            <Suspense fallback={<TabFallback />}>
              <DashboardReportsTab {...tabProps} />
            </Suspense>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export type DashboardTabSharedProps = {
  datePreset: DateRangePreset;
  refreshToken: number;
  isActive: boolean;
};
